/**
 * Las decisiones de responsive que se repiten en todas las pantallas.
 *
 * Están juntas porque son una sola decisión tomada muchas veces: qué se ve en
 * un teléfono de 360px y qué se guarda para la pantalla grande. Repartidas por
 * los archivos, cada pantalla terminaba con su propio criterio y el resultado
 * era una aplicación que en el celular se sentía de otra aplicación.
 *
 * El caso real: el operario del lavadero está en la entrada con el celular en
 * la mano, no sentado frente a un escritorio. Si para abrir una orden tiene
 * que hacer zoom y arrastrar de lado, no la abre.
 */
import { useMediaQuery, useTheme } from '@mui/material';

/** El teléfono. Por debajo de 600px no caben dos columnas de nada. */
export function useEsMovil() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
}

/** Tableta o menos: el menú lateral está cerrado. */
export function useEsAngosto() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
}

/** Campos de formulario que van uno al lado del otro… salvo en el teléfono. */
export const FILA = { xs: 'column', sm: 'row' };

/** Barra de filtros: en vez de desbordarse, baja a la línea siguiente. */
export const BARRA_FILTROS = {
  display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center',
};

/** Encabezado de pantalla: título y botón, apilados en el teléfono. */
export const ENCABEZADO = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'stretch', sm: 'center' },
  gap: 1.5,
  mb: 2,
};

/**
 * Un ancho mínimo para las tablas de muchas columnas.
 *
 * Sin él las celdas se comprimen hasta partir cada palabra en su propia línea
 * y la tabla se vuelve ilegible. Con él la tabla se desplaza de lado dentro de
 * su propio contenedor —no la página entera—, que es el comportamiento que la
 * gente ya conoce de cualquier hoja de cálculo en el celular.
 */
export const tablaAncha = (min = 760) => ({ minWidth: min });

/** Columna que solo aparece cuando hay espacio. */
export const SOLO_GRANDE = { display: { xs: 'none', md: 'table-cell' } };
export const SOLO_MEDIANO = { display: { xs: 'none', sm: 'table-cell' } };

/** Un campo de ancho fijo que en el teléfono ocupa todo el ancho. */
export const ancho = (n) => ({ width: { xs: '100%', sm: n } });

/**
 * Un control de la barra de filtros.
 *
 * En pantalla grande conserva su ancho; en el teléfono ocupa la línea entera.
 * Dejarlos a 140px en un celular produce una fila irregular de cajitas a
 * medio llenar que se lee peor que una debajo de otra.
 */
export const filtro = (n) => ({
  width: { xs: '100%', sm: 'auto' },
  minWidth: { xs: 0, sm: n },
});

/**
 * El botón de acción del encabezado.
 *
 * En el escritorio es un botón normal. En el teléfono, un «Nuevo servicio»
 * de tamaño medio con ícono ocupa media pantalla al lado de un título que ya
 * dice de qué pantalla se trata: se reduce de tamaño y el texto se acorta a
 * la acción sola, que es lo único que aporta.
 */
export const botonEncabezado = (esMovil) => ({
  size: esMovil ? 'small' : 'medium',
  sx: { flexShrink: 0, whiteSpace: 'nowrap',
        ...(esMovil ? { px: 1.25, fontSize: 12.5 } : {}) },
});

/** El texto largo solo cuando hay ancho para él. */
export const rotulo = (esMovil, corto, largo) => (esMovil ? corto : largo);
